/**
 * Meeting page interactive module.
 *
 * - Fetch availability via REST /meeting-availability
 * - Render calendar grid (1 month at a time) + time slots column
 * - Handle day click → fetch slots → render
 * - Handle slot click → POST /meeting-book → show success
 * - Poll /meeting-instant-available every 30s → toggle "Call NOW" card
 * - Handle "Call NOW" click → POST /meeting-instant-call → show modal
 *
 * v1.1.77 — Initial.
 */

import { Cfg } from './config.js';
import { apiGet, apiPost } from './api.js';

const POLL_INSTANT_MS = 30000;
const FETCH_AVAIL_CACHE_MS = 5 * 60 * 1000;

function $( id ) { return document.getElementById( id ); }
function txt( el, s ) { if ( el ) el.textContent = s; }
function show( el ) { if ( el ) el.style.display = ''; }
function hide( el ) { if ( el ) el.style.display = 'none'; }

const state = {
	currentMonth: null, // Date set to 1st of current month at midnight
	availability: {}, // { 'YYYY-MM-DD': ['10:00', ...] }
	availFetchedAt: 0,
	availFetchedRange: null,
	selectedDate: null, // 'YYYY-MM-DD'
	selectedSlot: null, // 'HH:MM'
	tz: 'America/Toronto',
	monthNamesFr: [ 'janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre' ],
	monthNamesEn: [ 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December' ],
};

function lang() { return ( Cfg.lang === 'en' ? 'en' : 'fr' ); }

function fmtMonthYear( d ) {
	const names = lang() === 'en' ? state.monthNamesEn : state.monthNamesFr;
	return names[ d.getMonth() ] + ' ' + d.getFullYear();
}

function fmtDateKey( d ) {
	const y = d.getFullYear();
	const m = String( d.getMonth() + 1 ).padStart( 2, '0' );
	const day = String( d.getDate() ).padStart( 2, '0' );
	return y + '-' + m + '-' + day;
}

function fmtDayLabel( dateKey ) {
	const [ y, m, d ] = dateKey.split( '-' ).map( Number );
	const dt = new Date( y, m - 1, d );
	const daysFr = [ 'Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi' ];
	const daysEn = [ 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday' ];
	const dayName = ( lang() === 'en' ? daysEn : daysFr )[ dt.getDay() ];
	const monthName = ( lang() === 'en' ? state.monthNamesEn : state.monthNamesFr )[ dt.getMonth() ];
	if ( lang() === 'en' ) {
		return dayName + ', ' + monthName + ' ' + d + ', ' + y;
	}
	return dayName + ' ' + d + ' ' + monthName + ' ' + y;
}

async function fetchAvailability( forceRefresh ) {
	if ( ! Cfg.meeting ) return;

	const now = Date.now();
	if ( ! forceRefresh && state.availFetchedAt && ( now - state.availFetchedAt ) < FETCH_AVAIL_CACHE_MS ) {
		return state.availability;
	}

	const today = new Date();
	const start = fmtDateKey( today );
	const endDate = new Date();
	endDate.setDate( endDate.getDate() + ( Cfg.meeting.advanceMaxDays || 30 ) );
	const end = fmtDateKey( endDate );

	try {
		const res = await apiGet( '/meeting-availability', { start, end } );
		if ( res && res.success ) {
			state.availability = res.slots || {};
			state.availFetchedAt = now;
			state.availFetchedRange = { start, end };
			if ( res.tz ) state.tz = res.tz;
			updateSlotsTz();
		}
	} catch ( e ) {
		console.warn( 'meeting availability fetch failed', e );
	}
	return state.availability;
}

function updateSlotsTz() {
	const el = $( 'meetingSlotsTz' );
	if ( el ) {
		txt( el, lang() === 'en'
			? 'All times shown in ' + state.tz
			: 'Heures affichées en ' + state.tz );
	}
}

function renderCalendar() {
	if ( ! state.currentMonth ) {
		state.currentMonth = new Date();
		state.currentMonth.setDate( 1 );
		state.currentMonth.setHours( 0, 0, 0, 0 );
	}

	const titleEl = $( 'meetingCalTitle' );
	if ( titleEl ) titleEl.textContent = fmtMonthYear( state.currentMonth );

	const grid = $( 'meetingCalGrid' );
	if ( ! grid ) return;
	grid.innerHTML = '';

	const year = state.currentMonth.getFullYear();
	const month = state.currentMonth.getMonth();
	const firstDay = new Date( year, month, 1 );
	const lastDay = new Date( year, month + 1, 0 );
	const today = new Date();
	today.setHours( 0, 0, 0, 0 );

	// v1.1.80 — Dimanche = 0 (notre grille commence le dimanche, convention NA).
	const firstDow = firstDay.getDay();
	// Check if any day in this month has slots (used for empty-state message).
	let anyAvail = false;
	for ( const key in state.availability ) {
		if ( state.availability[ key ] && state.availability[ key ].length ) { anyAvail = true; break; }
	}

	// Cellules vides avant le 1er.
	for ( let i = 0; i < firstDow; i++ ) {
		const empty = document.createElement( 'span' );
		empty.className = 'meeting-cal-cell meeting-cal-cell-empty';
		grid.appendChild( empty );
	}

	for ( let d = 1; d <= lastDay.getDate(); d++ ) {
		const cellDate = new Date( year, month, d );
		const cellKey = fmtDateKey( cellDate );
		const dow = cellDate.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
		const isWeekend = dow === 0 || dow === 6;
		const isPast = cellDate < today;
		const hasSlots = !! ( state.availability[ cellKey ] && state.availability[ cellKey ].length );

		const btn = document.createElement( 'button' );
		btn.type = 'button';
		btn.className = 'meeting-cal-cell';
		btn.textContent = String( d );
		btn.setAttribute( 'data-date', cellKey );

		if ( isWeekend ) {
			btn.classList.add( 'meeting-cal-cell-weekend' );
			btn.disabled = true;
		} else if ( isPast ) {
			btn.classList.add( 'meeting-cal-cell-past' );
			btn.disabled = true;
		} else if ( hasSlots ) {
			btn.classList.add( 'meeting-cal-cell-avail' );
			btn.addEventListener( 'click', () => onDayClick( cellKey ) );
		} else {
			// Jour business sans slot dispo : visuel neutre (pas not-allowed agressif).
			btn.classList.add( 'meeting-cal-cell-noslot' );
			btn.disabled = true;
		}
		if ( state.selectedDate === cellKey ) {
			btn.classList.add( 'meeting-cal-cell-selected' );
		}
		grid.appendChild( btn );
	}

	// v1.1.80 — Si l'admin n'a aucun jour dispo dans la fenêtre fetchée,
	// affiche un message friendly à la place du calendrier rouge-disabled.
	if ( ! anyAvail && state.availFetchedAt > 0 ) {
		const head = $( 'meetingSlotsHead' );
		const list = $( 'meetingSlotsList' );
		if ( head ) {
			head.innerHTML = lang() === 'en'
				? '<span style="color:#9CA3AF">Calendar not yet configured</span>'
				: '<span style="color:#9CA3AF">Calendrier non encore configuré</span>';
		}
		if ( list ) {
			list.innerHTML = '<div class="meeting-slots-empty">'
				+ ( lang() === 'en'
					? 'No availability shown for this period. Please try a later month.'
					: 'Aucune disponibilité affichée pour cette période. Essayez un mois plus tard.' )
				+ '</div>';
		}
	}
}

function onDayClick( dateKey ) {
	state.selectedDate = dateKey;
	state.selectedSlot = null;
	renderCalendar();
	renderSlots();
}

function renderSlots() {
	const head = $( 'meetingSlotsHead' );
	const list = $( 'meetingSlotsList' );
	if ( ! head || ! list ) return;

	if ( ! state.selectedDate ) {
		head.innerHTML = '<span data-fr="Sélectionnez un jour à gauche" data-en="Pick a day on the left">' +
			( lang() === 'en' ? 'Pick a day on the left' : 'Sélectionnez un jour à gauche' ) + '</span>';
		list.innerHTML = '<div class="meeting-slots-empty">' + ( lang() === 'en' ? 'No day selected.' : 'Aucun jour sélectionné.' ) + '</div>';
		updateNextButton();
		return;
	}

	head.textContent = fmtDayLabel( state.selectedDate );
	const slots = state.availability[ state.selectedDate ] || [];
	list.innerHTML = '';

	if ( slots.length === 0 ) {
		list.innerHTML = '<div class="meeting-slots-empty">' + ( lang() === 'en' ? 'No slots available this day.' : 'Aucun créneau disponible.' ) + '</div>';
		updateNextButton();
		return;
	}

	slots.forEach( ( slot ) => {
		const btn = document.createElement( 'button' );
		btn.type = 'button';
		btn.className = 'meeting-slot';
		btn.textContent = slot;
		btn.setAttribute( 'data-slot', slot );
		if ( state.selectedSlot === slot ) {
			btn.classList.add( 'meeting-slot-selected' );
		}
		btn.addEventListener( 'click', () => onSlotClick( slot ) );
		list.appendChild( btn );
	} );
	updateNextButton();
}

function onSlotClick( slot ) {
	state.selectedSlot = slot;
	renderSlots();
}

function updateNextButton() {
	const btn = $( 'btnNx2' );
	if ( btn ) {
		btn.disabled = !( state.selectedDate && state.selectedSlot );
	}
}

function buildSlotIso() {
	if ( ! state.selectedDate || ! state.selectedSlot ) return null;
	// ISO local au timezone du rep — Google Calendar parse correctement.
	// On délègue la TZ au serveur en envoyant juste 'YYYY-MM-DDTHH:MM:00' + offset best-effort.
	const offsetStr = ( function () {
		try {
			const dt = new Date( state.selectedDate + 'T' + state.selectedSlot + ':00' );
			const offsetMin = -dt.getTimezoneOffset();
			const sign = offsetMin >= 0 ? '+' : '-';
			const abs = Math.abs( offsetMin );
			return sign + String( Math.floor( abs / 60 ) ).padStart( 2, '0' ) + ':' + String( abs % 60 ).padStart( 2, '0' );
		} catch ( e ) { return 'Z'; }
	} )();
	return state.selectedDate + 'T' + state.selectedSlot + ':00' + offsetStr;
}

async function submitBooking() {
	const slot_iso = buildSlotIso();
	if ( ! slot_iso ) return;

	// Récupère les données contact du state global (panels.js gère le state).
	const S = window.ProbotSoumApp && window.ProbotSoumApp.S ? window.ProbotSoumApp.S : {};
	const payload = {
		name: ( S.firstName || '' ) + ' ' + ( S.lastName || '' ),
		email: S.email || '',
		phone_e164: S.phoneE164 || S.phone || '',
		company: S.company || '',
		subject: S.meetingSubject || '',
		slot_iso: slot_iso,
		lang: lang(),
	};

	const btn = $( 'btnNx2' );
	if ( btn ) {
		btn.disabled = true;
		btn.classList.add( 'is-loading' );
	}

	try {
		const res = await apiPost( '/meeting-book', payload, { botAction: 'meeting_book' } );
		if ( res && res.success ) {
			// Avance au step success — délégué au router de panels.js.
			if ( window.ProbotSoumApp && window.ProbotSoumApp.gotoSuccess ) {
				window.ProbotSoumApp.gotoSuccess( res );
			} else {
				const main = document.querySelector( '.ps-page' );
				if ( main ) {
					document.querySelectorAll( '.panel' ).forEach( ( p ) => p.style.display = 'none' );
					const p4 = $( 'p4' ) || $( 'p3' );
					if ( p4 ) p4.style.display = '';
				}
			}
		} else {
			alert( ( res && res.message ) || ( lang() === 'en' ? 'Booking failed. Try again.' : 'Échec de la réservation. Réessayez.' ) );
			if ( btn ) {
				btn.disabled = false;
				btn.classList.remove( 'is-loading' );
			}
			// Refresh availability au cas où le slot a été pris.
			await fetchAvailability( true );
			renderCalendar();
			renderSlots();
		}
	} catch ( e ) {
		console.warn( 'meeting-book failed', e );
		alert( lang() === 'en' ? 'Network error. Try again.' : 'Erreur réseau. Réessayez.' );
		if ( btn ) {
			btn.disabled = false;
			btn.classList.remove( 'is-loading' );
		}
	}
}

async function pollInstantAvailable() {
	if ( ! Cfg.meeting || ! Cfg.meeting.instantEnabled ) return;
	try {
		const res = await apiGet( '/meeting-instant-available' );
		const card = $( 'meetingInstantCard' );
		const repName = $( 'meetingInstantRepName' );
		if ( repName && Cfg.meeting.repName ) {
			repName.textContent = Cfg.meeting.repName;
		}
		if ( card ) {
			if ( res && res.available ) {
				show( card );
			} else {
				hide( card );
			}
		}
	} catch ( e ) {
		console.warn( 'instant-available poll failed', e );
	}
}

async function triggerInstantCall() {
	const S = window.ProbotSoumApp && window.ProbotSoumApp.S ? window.ProbotSoumApp.S : {};
	const payload = {
		name: ( S.firstName || '' ) + ' ' + ( S.lastName || '' ),
		email: S.email || '',
		phone_e164: S.phoneE164 || S.phone || '',
		company: S.company || '',
		subject: S.meetingSubject || '',
	};

	const btn = $( 'btnMeetingInstantCall' );
	if ( btn ) {
		btn.disabled = true;
		btn.classList.add( 'is-loading' );
	}

	try {
		const res = await apiPost( '/meeting-instant-call', payload, { botAction: 'meeting_instant_call' } );
		if ( res && res.success ) {
			alert( lang() === 'en'
				? '📞 Call initiated. Your phone will ring in ~5 seconds. The rep will be on the line.'
				: '📞 Appel lancé. Ton téléphone va sonner dans ~5 secondes. Le rep sera en ligne.' );
		} else {
			alert( ( res && res.message ) || ( lang() === 'en' ? 'Call failed.' : 'L\'appel a échoué.' ) );
		}
	} catch ( e ) {
		console.warn( 'instant-call failed', e );
		alert( lang() === 'en' ? 'Network error.' : 'Erreur réseau.' );
	} finally {
		if ( btn ) {
			btn.disabled = false;
			btn.classList.remove( 'is-loading' );
		}
	}
}

function navMonth( delta ) {
	if ( ! state.currentMonth ) return;
	state.currentMonth = new Date( state.currentMonth.getFullYear(), state.currentMonth.getMonth() + delta, 1 );
	renderCalendar();
}

/**
 * v1.1.79 — Initialize popup mode listeners (open / close / ESC).
 * Hook this independently of initMeeting() so the trigger button works
 * even before the user opens the modal.
 */
function initPopup() {
	const triggers = document.querySelectorAll( '[data-meeting-popup-open]' );
	const closeBtns = document.querySelectorAll( '[data-meeting-popup-close]' );
	const modal = document.getElementById( 'probotMeetingModal' );
	if ( ! modal || triggers.length === 0 ) return;

	function openModal() {
		modal.hidden = false;
		document.body.classList.add( 'probot-meeting-modal-open' );
		// Initialize the meeting form when first opened (fetch availability).
		if ( ! modal.dataset.initialized ) {
			modal.dataset.initialized = '1';
			initMeeting();
		}
	}
	function closeModal() {
		modal.hidden = true;
		document.body.classList.remove( 'probot-meeting-modal-open' );
	}

	triggers.forEach( ( btn ) => btn.addEventListener( 'click', openModal ) );
	closeBtns.forEach( ( btn ) => btn.addEventListener( 'click', closeModal ) );
	document.addEventListener( 'keydown', ( e ) => {
		if ( e.key === 'Escape' && ! modal.hidden ) closeModal();
	} );
}

export function initMeeting() {
	if ( document.querySelector( '[data-quote-type="meeting"]' ) === null ) return;
	if ( ! Cfg.meeting ) {
		console.warn( 'Meeting config missing — ensure shortcode is on the page' );
		return;
	}

	// Hook calendar nav.
	const prev = $( 'meetingCalPrev' );
	const next = $( 'meetingCalNext' );
	if ( prev ) prev.addEventListener( 'click', () => navMonth( -1 ) );
	if ( next ) next.addEventListener( 'click', () => navMonth( 1 ) );

	// Hook next button → submit booking.
	const nx2 = $( 'btnNx2' );
	if ( nx2 ) nx2.addEventListener( 'click', submitBooking );

	// Hook instant call button.
	const instantBtn = $( 'btnMeetingInstantCall' );
	if ( instantBtn ) instantBtn.addEventListener( 'click', triggerInstantCall );

	// Initial fetch + render.
	updateSlotsTz();
	fetchAvailability( false ).then( () => {
		renderCalendar();
		renderSlots();
	} );

	// Start polling for instant availability.
	pollInstantAvailable();
	setInterval( pollInstantAvailable, POLL_INSTANT_MS );
}

// Export so main.js can call it on every page load — checks itself if modal exists.
export { initPopup };

